import type { AttributeDefinitionConfig } from '@/lib/decorators/attribute-definition';
import { ANALYTICS_CONTENT_GROUP_ATTRIBUTE_ID, CONTENT_GROUP, type ContentGroupValue } from './content-group';

export { ANALYTICS_CONTENT_GROUP_ATTRIBUTE_ID };

/** Location pages — store or restaurant detail only. */
export const LOCATION_PAGE_CONTENT_GROUP_VALUES = [
    CONTENT_GROUP.STORE_DETAILS,
    CONTENT_GROUP.RESTAURANT_DETAILS,
] as const satisfies readonly ContentGroupValue[];

/** Content pillar pages — home pillar navigation variants only. */
export const CONTENT_PILLAR_PAGE_CONTENT_GROUP_VALUES = [
    CONTENT_GROUP.HOME_SPLASH,
    CONTENT_GROUP.HOME_ONLINE_SHOP,
    CONTENT_GROUP.HOME_EVENTS,
    CONTENT_GROUP.HOME_CLASSES,
    CONTENT_GROUP.HOME_RESTAURANTS,
] as const satisfies readonly ContentGroupValue[];

/** Generic editorial pages — listings, detail pages, and institutional content. */
export const GENERIC_CONTENT_PAGE_CONTENT_GROUP_VALUES = [
    CONTENT_GROUP.STORE_LIST,
    CONTENT_GROUP.RESTAURANT_LIST,
    CONTENT_GROUP.CLASS_LIST,
    CONTENT_GROUP.EVENT_LIST,
    CONTENT_GROUP.NEWS_LIST,
    CONTENT_GROUP.JOB_LIST,
    CONTENT_GROUP.RECIPES,
    CONTENT_GROUP.NEWS_DETAILS,
    CONTENT_GROUP.JOB_DETAILS,
    CONTENT_GROUP.MAKERS,
    CONTENT_GROUP.BLOG,
    CONTENT_GROUP.CLASS_DETAILS,
    CONTENT_GROUP.EVENT_DETAILS,
    CONTENT_GROUP.SERVICE,
    CONTENT_GROUP.SERVICES,
    CONTENT_GROUP.BUSINESS,
    CONTENT_GROUP.GIFT_HUB,
    CONTENT_GROUP.SEASONAL,
    CONTENT_GROUP.ABOUT_US,
    CONTENT_GROUP.WORK_WITH_US,
    CONTENT_GROUP.HELP,
    CONTENT_GROUP.NEWSLETTER,
    CONTENT_GROUP.CLUB,
] as const satisfies readonly ContentGroupValue[];

function buildAnalyticsContentGroupDefinition(
    values: readonly ContentGroupValue[],
    description: string
): AttributeDefinitionConfig {
    return {
        id: ANALYTICS_CONTENT_GROUP_ATTRIBUTE_ID,
        name: 'Analytics Content Group',
        type: 'enum',
        required: true,
        values: [...values],
        description,
    };
}

export const locationPageAnalyticsContentGroupDefinition = buildAnalyticsContentGroupDefinition(
    LOCATION_PAGE_CONTENT_GROUP_VALUES,
    'GA4 content_group for page_view tracking. Choose store details for store locations, restaurant details for restaurant locations.'
);

const PILLAR_HOME_ANALYTICS_CONTENT_GROUP_DESCRIPTION =
    'GA4 content_group for home pillar pages. Match the active pillar tab: splash, online shop, events, classes, or restaurants.';

export const contentPillarPageAnalyticsContentGroupDefinition = buildAnalyticsContentGroupDefinition(
    CONTENT_PILLAR_PAGE_CONTENT_GROUP_VALUES,
    PILLAR_HOME_ANALYTICS_CONTENT_GROUP_DESCRIPTION
);

/** Category-bound pillar pages (`pillarPage`, `fullEditorialCategoryPage`) — same home pillar enum as content pillar LPs. */
export const categoryPillarPageAnalyticsContentGroupDefinition = buildAnalyticsContentGroupDefinition(
    CONTENT_PILLAR_PAGE_CONTENT_GROUP_VALUES,
    `${PILLAR_HOME_ANALYTICS_CONTENT_GROUP_DESCRIPTION} Set on the Page Designer page assigned to the category.`
);

export const genericContentPageAnalyticsContentGroupDefinition = buildAnalyticsContentGroupDefinition(
    GENERIC_CONTENT_PAGE_CONTENT_GROUP_VALUES,
    'GA4 content_group for editorial pages. Use * list for folder listings, * details for articles, services/business/gift hub/seasonal for LPs, service for legal/privacy, help for FAQ.'
);

export type { ContentGroupValue };
